import { KeyboardEvent, MouseEvent } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import { QuerySource } from '../../../../types/QueryBuilder';

interface FilterGroup {
    id: number;
    description: string;
}

interface FilterHeaderProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onSearchSubmit: (value: string) => void;
    selectedGroupIds: number[];
    onSelectedGroupIdsChange: (ids: number[]) => void;
    availableGroups: FilterGroup[];
    loadingGroups: boolean;
    isFromCompany: 0 | 1 | undefined;
    onTypeChange: (value: 0 | 1 | undefined) => void;
    source: QuerySource | undefined;
    onSourceChange: (value: QuerySource | undefined) => void;
    onReset: () => void;
}

export function FilterHeader({
    searchTerm,
    onSearchChange,
    onSearchSubmit,
    selectedGroupIds,
    onSelectedGroupIdsChange,
    availableGroups,
    loadingGroups,
    isFromCompany,
    onTypeChange,
    source,
    onSourceChange,
    onReset,
}: FilterHeaderProps) {
    const { t } = useLanguage();
    const handleTypeChange = (_: MouseEvent<HTMLElement>, value: 0 | 1 | null) => onTypeChange(value ?? undefined);
    const handleSourceChange = (_: MouseEvent<HTMLElement>, value: QuerySource | null) => onSourceChange(value ?? undefined);
    const handleSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') onSearchSubmit(searchTerm);
    };

    const activeGroupChips = selectedGroupIds
        .map(groupId => availableGroups.find(g => g.id === groupId))
        .filter((group): group is FilterGroup => !!group);

    const hasActiveFilters = activeGroupChips.length > 0 || isFromCompany !== undefined || source !== undefined;

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>{t('pages.clients.queryBuilder.filterHeader.groupsLabel')}</InputLabel>
                        <Select
                            multiple
                            value={selectedGroupIds}
                            onChange={(e) => onSelectedGroupIdsChange(e.target.value as number[])}
                            input={<OutlinedInput label={t('pages.clients.queryBuilder.filterHeader.groupsLabel')} />}
                            disabled={loadingGroups}
                            renderValue={(selected) => {
                                if (selected.length === 0) return <em>{t('pages.clients.queryBuilder.filterHeader.allGroups')}</em>;
                                return t(
                                    selected.length > 1
                                        ? 'pages.clients.queryBuilder.filterHeader.groupsSelectedMany'
                                        : 'pages.clients.queryBuilder.filterHeader.groupsSelectedOne',
                                    { count: selected.length }
                                );
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        >
                            {availableGroups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    <Checkbox checked={selectedGroupIds.includes(group.id)} />
                                    <ListItemText primary={group.description} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                        {t('pages.clients.queryBuilder.filterByType')}
                    </Typography>
                    <ToggleButtonGroup
                        value={isFromCompany}
                        exclusive
                        onChange={handleTypeChange}
                        size="small"
                    >
                        <ToggleButton value={1} sx={{ textTransform: 'none', px: 2 }}>{t('pages.clients.queryBuilder.brand')}</ToggleButton>
                        <ToggleButton value={0} sx={{ textTransform: 'none', px: 2 }}>{t('pages.clients.queryBuilder.personal')}</ToggleButton>
                    </ToggleButtonGroup>

                    <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                        {t('pages.clients.queryBuilder.filterBySource')}
                    </Typography>
                    <ToggleButtonGroup
                        value={source}
                        exclusive
                        onChange={handleSourceChange}
                        size="small"
                    >
                        <ToggleButton value="SIMPLE" sx={{ textTransform: 'none', px: 2 }}>{t('pages.clients.queryBuilder.myQueriesTab.simple')}</ToggleButton>
                        <ToggleButton value="ADVANCED" sx={{ textTransform: 'none', px: 2 }}>{t('pages.clients.queryBuilder.myQueriesTab.advanced')}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Button size="small" startIcon={<RefreshIcon />} onClick={onReset} sx={{ textTransform: 'none' }}>
                    {t('pages.clients.queryBuilder.reset')}
                </Button>
            </Box>

            {hasActiveFilters && (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
                    {activeGroupChips.map((group) => (
                        <Chip
                            key={group.id}
                            label={group.description}
                            onDelete={() => onSelectedGroupIdsChange(selectedGroupIds.filter(id => id !== group.id))}
                            color="primary"
                            size="small"
                        />
                    ))}
                    {isFromCompany !== undefined && (
                        <Chip
                            label={isFromCompany === 1 ? t('pages.clients.queryBuilder.brand') : t('pages.clients.queryBuilder.personal')}
                            onDelete={() => onTypeChange(undefined)}
                            color="primary"
                            size="small"
                        />
                    )}
                    {source !== undefined && (
                        <Chip
                            label={source === 'ADVANCED' ? t('pages.clients.queryBuilder.myQueriesTab.advanced') : t('pages.clients.queryBuilder.myQueriesTab.simple')}
                            onDelete={() => onSourceChange(undefined)}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>
            )}
        </Paper>
    );
}
